import React, { useCallback, useEffect, useState } from 'react';
import { ArrowPathIcon, XMarkIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { getAdminOrder, setAdminOrderStatus, updateAdminOrder } from '../../api/adminOrders';
import {
  ORDER_STATUSES,
  PersonCard,
  PaymentBadge,
  StatusBadge,
  fmtDateTime,
  fmtMoney,
  inputCls,
  paymentMethodLabel,
  statusMeta,
} from './ui';

const TRANSACTION_TYPES = {
  ORDER_PAYMENT: 'Оплата заказа',
  COMMISSION: 'Комиссия',
  REFUND: 'Возврат',
  BUSINESS_CREDIT: 'Зачисление продавцу',
};

const toDateInput = (iso) => (iso ? iso.slice(0, 10) : '');

const Row = ({ label, children }) => (
  <div className="flex items-start justify-between gap-3 py-1 text-sm">
    <span className="text-text-secondary">{label}</span>
    <span className="text-right font-medium text-text-primary">{children}</span>
  </div>
);

const Section = ({ title, children }) => (
  <section className="border-t border-border-primary px-5 py-4">
    <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-text-muted">{title}</h4>
    {children}
  </section>
);

/** Карточка заказа: состав, суммы, участники, история и ручное управление статусом. */
const OrderDetailsPanel = ({ orderId, onClose, onUpdated, onOpenUser, notify }) => {
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [newStatus, setNewStatus] = useState('');
  const [note, setNote] = useState('');
  const [refund, setRefund] = useState(true);
  const [notifyUsers, setNotifyUsers] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ notes: '', delivery_address: '', delivery_date: '' });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getAdminOrder(orderId);
      setOrder(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    load();
  }, [load]);

  const applyOrder = (updated) => {
    setOrder(updated);
    onUpdated?.(updated);
  };

  const submitStatus = async () => {
    setBusy(true);
    try {
      const res = await setAdminOrderStatus(order.id, {
        status: newStatus,
        note: note.trim() || null,
        refund,
        notify: notifyUsers,
      });
      applyOrder(res.order);
      notify(
        'success',
        `Заказ #${order.id}: статус «${statusMeta(newStatus).label}»${res.refunded ? ', деньги возвращены на баланс покупателя' : ''}`
      );
      setNewStatus('');
      setNote('');
      setConfirming(false);
    } catch (err) {
      notify('error', `Не удалось сменить статус: ${err.detail || err.message}`);
    } finally {
      setBusy(false);
    }
  };

  const startEdit = () => {
    setForm({
      notes: order.notes || '',
      delivery_address: order.delivery_address || '',
      delivery_date: toDateInput(order.delivery_date),
    });
    setEditing(true);
  };

  const saveEdit = async () => {
    setBusy(true);
    try {
      const res = await updateAdminOrder(order.id, {
        notes: form.notes,
        delivery_address: form.delivery_address,
        delivery_date: form.delivery_date || null,
      });
      applyOrder(res.order);
      setEditing(false);
      notify('success', `Заказ #${order.id} обновлён`);
    } catch (err) {
      notify('error', `Не удалось сохранить: ${err.detail || err.message}`);
    } finally {
      setBusy(false);
    }
  };

  if (loading && !order) {
    return (
      <div className="flex items-center justify-center px-6 py-16 text-sm text-text-secondary">
        <ArrowPathIcon className="mr-2 h-4 w-4 animate-spin" /> Загрузка заказа…
      </div>
    );
  }
  if (error) {
    return (
      <div className="px-6 py-12 text-center">
        <p className="mb-4 text-sm text-rose-500">{error}</p>
        <button type="button" onClick={load} className="rounded-lg border border-border-primary px-4 py-2 text-sm text-text-secondary hover:bg-bg-tertiary">
          Повторить
        </button>
      </div>
    );
  }
  if (!order) return null;

  const isPaid = order.payment_status === 'paid';
  const willCancelPaid = newStatus === 'cancelled' && isPaid;
  const lockedRefunded = order.status === 'cancelled' && order.payment_status === 'refunded';

  return (
    <div className="text-left">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 px-5 py-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-bold text-text-primary">Заказ #{order.id}</h3>
            <button type="button" onClick={load} title="Обновить" className="rounded p-1 text-text-muted hover:bg-bg-tertiary">
              <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
          <div className="mt-1 flex flex-wrap gap-1.5">
            <StatusBadge status={order.status} />
            <PaymentBadge status={order.payment_status} />
          </div>
          <p className="mt-1 text-xs text-text-muted">Создан {fmtDateTime(order.created_at)}</p>
        </div>
        <button type="button" onClick={onClose} aria-label="Закрыть" className="rounded-lg p-1.5 text-text-muted hover:bg-bg-tertiary">
          <XMarkIcon className="h-5 w-5" />
        </button>
      </div>

      <Section title="Участники">
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <PersonCard title="Покупатель" person={order.buyer} onOpenUser={onOpenUser} />
          <PersonCard title="Продавец" person={order.supplier} onOpenUser={onOpenUser} />
        </div>
      </Section>

      <Section title={`Товары (${order.items?.length || 0})`}>
        {order.items?.length ? (
          <ul className="space-y-2">
            {order.items.map((it) => (
              <li key={it.id} className="flex items-center gap-3">
                {it.main_image_url ? (
                  <img src={it.main_image_url} alt="" className="h-10 w-10 shrink-0 rounded-lg object-cover" />
                ) : (
                  <div className="h-10 w-10 shrink-0 rounded-lg bg-bg-tertiary" />
                )}
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium text-text-primary">{it.title || `Объявление #${it.announcement_id}`}</div>
                  <div className="text-xs text-text-secondary">
                    {it.quantity} {it.measurement_unit || ''} × {fmtMoney(it.unit_price)}
                  </div>
                </div>
                <span className="shrink-0 text-sm font-semibold text-text-primary">{fmtMoney(it.line_subtotal)}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-text-muted">Позиции не указаны{order.announcement_id ? ` — объявление #${order.announcement_id}` : ''}</p>
        )}
      </Section>

      <Section title="Суммы">
        <Row label="Товары">{fmtMoney(order.subtotal)}</Row>
        <Row label="Доставка">{fmtMoney(order.delivery_fee)}</Row>
        {order.vat_amount > 0 && <Row label={`НДС${order.vat_rate ? ` ${order.vat_rate}%` : ''}`}>{fmtMoney(order.vat_amount)}</Row>}
        <Row label="Итого">
          <span className="text-base">{fmtMoney(order.total_amount)}</span>
        </Row>
        {order.commission_amount !== null && order.commission_amount !== undefined && (
          <Row label={`Комиссия Agroland${order.commission_rate ? ` (${order.commission_rate}%)` : ''}`}>{fmtMoney(order.commission_amount)}</Row>
        )}
        {order.dealer_payout !== null && order.dealer_payout !== undefined && <Row label="Продавцу">{fmtMoney(order.dealer_payout)}</Row>}
        <Row label="Способ оплаты">{paymentMethodLabel(order.payment_method)}</Row>
        {order.paid_at && <Row label="Оплачен">{fmtDateTime(order.paid_at)}</Row>}
      </Section>

      <Section title="Доставка">
        {editing ? (
          <div className="space-y-2">
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-text-secondary">Адрес доставки</span>
              <input value={form.delivery_address} onChange={(e) => setForm((f) => ({ ...f, delivery_address: e.target.value }))} className={inputCls} />
            </label>
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-text-secondary">Дата доставки</span>
              <input type="date" value={form.delivery_date} onChange={(e) => setForm((f) => ({ ...f, delivery_date: e.target.value }))} className={inputCls} />
            </label>
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-text-secondary">Комментарий</span>
              <textarea rows={3} value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} className={inputCls} />
            </label>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setEditing(false)} disabled={busy} className="rounded-lg border border-border-primary px-3 py-1.5 text-sm text-text-secondary hover:bg-bg-tertiary">
                Отмена
              </button>
              <button type="button" onClick={saveEdit} disabled={busy} className="rounded-lg bg-accent-grad px-3 py-1.5 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50">
                Сохранить
              </button>
            </div>
          </div>
        ) : (
          <>
            <Row label="Способ">{order.pickup ? 'Самовывоз' : order.delivery_method === 'alemtat' ? 'Алем ТАТ' : 'Продавец'}</Row>
            {order.delivery_zone_name && <Row label="Зона">{order.delivery_zone_name}</Row>}
            <Row label="Адрес">{order.delivery_address || '—'}</Row>
            <Row label="Дата доставки">{order.delivery_date ? fmtDateTime(order.delivery_date) : '—'}</Row>
            {order.notes && <p className="mt-2 rounded-lg bg-bg-tertiary/60 px-3 py-2 text-sm text-text-secondary">{order.notes}</p>}
            <button type="button" onClick={startEdit} className="mt-2 text-xs font-medium text-[#7C3AED] hover:underline dark:text-indigo-300">
              Изменить адрес / дату / комментарий
            </button>
          </>
        )}
      </Section>

      <Section title="Управление статусом">
        {lockedRefunded ? (
          <p className="text-sm text-text-secondary">Заказ отменён, деньги возвращены покупателю — повторно открыть нельзя.</p>
        ) : (
          <div className="space-y-2">
            <select
              aria-label="Новый статус"
              value={newStatus}
              onChange={(e) => {
                setNewStatus(e.target.value);
                setConfirming(false);
              }}
              className={inputCls}
            >
              <option value="">Выберите новый статус…</option>
              {ORDER_STATUSES.filter((s) => s.value !== order.status).map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
            {newStatus && (
              <>
                <textarea
                  rows={2}
                  placeholder="Причина / комментарий (видно в истории)"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  className={inputCls}
                />
                {willCancelPaid && (
                  <label className="flex items-center gap-2 text-sm text-text-secondary">
                    <input type="checkbox" checked={refund} onChange={(e) => setRefund(e.target.checked)} />
                    Вернуть {fmtMoney(order.total_amount)} на баланс покупателя
                  </label>
                )}
                <label className="flex items-center gap-2 text-sm text-text-secondary">
                  <input type="checkbox" checked={notifyUsers} onChange={(e) => setNotifyUsers(e.target.checked)} />
                  Отправить push участникам
                </label>
                {!confirming ? (
                  <button
                    type="button"
                    onClick={() => setConfirming(true)}
                    className="w-full rounded-lg bg-accent-grad px-4 py-2 text-sm font-semibold text-white hover:opacity-90"
                  >
                    Сменить статус
                  </button>
                ) : (
                  <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3">
                    <p className="mb-2 flex items-start gap-2 text-sm text-amber-600 dark:text-amber-400">
                      <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0" />
                      <span>
                        «{statusMeta(order.status).label}» → «{statusMeta(newStatus).label}». Статус ставится вручную, минуя обычный порядок.
                        {willCancelPaid && refund && ' Деньги вернутся на баланс покупателя, у продавца будут списаны.'}
                      </span>
                    </p>
                    <div className="flex justify-end gap-2">
                      <button type="button" onClick={() => setConfirming(false)} disabled={busy} className="rounded-lg border border-border-primary px-3 py-1.5 text-sm text-text-secondary hover:bg-bg-tertiary">
                        Отмена
                      </button>
                      <button type="button" onClick={submitStatus} disabled={busy} className="rounded-lg bg-accent-grad px-3 py-1.5 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50">
                        {busy ? 'Сохранение…' : 'Подтвердить'}
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </Section>

      <Section title="История">
        {order.history?.length ? (
          <ol className="space-y-2">
            {order.history.map((h) => (
              <li key={h.id} className="flex gap-3 text-sm">
                <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-[#4F45E5]" />
                <div className="min-w-0">
                  <div className="font-medium text-text-primary">{statusMeta(h.status).label}</div>
                  <div className="text-xs text-text-muted">
                    {fmtDateTime(h.created_at)}
                    {h.changed_by && ` · ${h.changed_by_name || 'пользователь'} #${h.changed_by}`}
                  </div>
                  {h.note && <div className="text-xs text-text-secondary">{h.note}</div>}
                </div>
              </li>
            ))}
          </ol>
        ) : (
          <p className="text-sm text-text-muted">Нет записей</p>
        )}
      </Section>

      {order.transactions?.length > 0 && (
        <Section title="Движение денег">
          <ul className="space-y-1">
            {order.transactions.map((t) => (
              <li key={t.id} className="flex items-center justify-between gap-3 text-sm">
                <span className="text-text-secondary">
                  {TRANSACTION_TYPES[t.type] || t.type} · #{t.user_id}
                  <span className="ml-1 text-xs text-text-muted">{fmtDateTime(t.created_at)}</span>
                </span>
                <span className={`font-medium ${t.amount < 0 ? 'text-rose-500' : 'text-emerald-500'}`}>{fmtMoney(t.amount)}</span>
              </li>
            ))}
          </ul>
        </Section>
      )}
    </div>
  );
};

export default OrderDetailsPanel;
